import { RECEIVABLE_PAYMENT_METHODS } from "@artisancode/api-types";

import { DatePicker } from "@/components/shared/date-picker";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/ui/number-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import { PAYMENT_METHOD_LABELS } from "./receivable-labels";

import type { PaymentFormValues } from "./receivable-schema";
import type { BaseSyntheticEvent } from "react";
import type { UseFormReturn } from "react-hook-form";

export function ReceivableNewPaymentForm({
  paymentForm,
  onSubmitPayment,
  previewSeq,
  canCreate,
  canVerify,
  isRecording,
}: {
  paymentForm: UseFormReturn<PaymentFormValues>;
  onSubmitPayment: (e?: BaseSyntheticEvent) => Promise<void>;
  previewSeq: string;
  canCreate: boolean;
  canVerify: boolean;
  isRecording: boolean;
}) {
  return (
    <Form {...paymentForm}>
      <form
        onSubmit={onSubmitPayment}
        className="space-y-3 rounded-lg border p-3.5"
      >
        <Label className="text-sm font-semibold">Catat Pembayaran Baru</Label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
          <FormField
            control={paymentForm.control}
            name="date"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">Tanggal</FormLabel>
                <FormControl>
                  <DatePicker value={field.value} onChange={field.onChange} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={paymentForm.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">Nominal</FormLabel>
                <FormControl>
                  <NumberInput
                    value={field.value ?? 0}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={paymentForm.control}
            name="method"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">Metode</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {RECEIVABLE_PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m} value={m}>
                        {PAYMENT_METHOD_LABELS[m]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          {canVerify && (
            <FormField
              control={paymentForm.control}
              name="verified"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-2 pt-2 sm:pt-6">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(v) => field.onChange(Boolean(v))}
                    />
                  </FormControl>
                  <FormLabel className="text-xs font-normal">
                    Langsung Terverifikasi
                  </FormLabel>
                </FormItem>
              )}
            />
          )}
        </div>
        <FormField
          control={paymentForm.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel className="text-xs">
                  Catatan / No. Kuitansi
                </FormLabel>
                {previewSeq && (
                  <span className="text-[11px] font-mono text-muted-foreground">
                    Otomatis: {previewSeq}
                  </span>
                )}
              </div>
              <FormControl>
                <Textarea
                  placeholder={
                    previewSeq
                      ? `Kosongkan untuk otomatis (${previewSeq})`
                      : "Catatan / No. Bukti Pembayaran"
                  }
                  className="min-h-16 resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" size="sm" disabled={!canCreate || isRecording}>
          {isRecording ? "Menyimpan..." : "Catat Pembayaran"}
        </Button>
      </form>
    </Form>
  );
}
